import { get } from "lodash-es";

export const CONFLUX_GENERIC_CREDIT_CARD_CODE = "CREDIT_CARD";

export const CONFLUX_CARD_PAYMENT_CODES = [
  "CARD",
  CONFLUX_GENERIC_CREDIT_CARD_CODE,
  "VISA",
  "MASTER",
  "MASTERCARD",
  "JCB",
  "AMEX",
  "AMERICAN_EXPRESS",
  "DISCOVER",
];

export const CONFLUX_WALLET_PAYMENT_CODES = [
  "APPLE_PAY",
  "GOOGLE_PAY",
  "KLARNA",
  "CASHAPP_WAP",
  "CASHAPP_WEB",
  "CASH_APP",
  "PAYPAL",
  "SHOP_PAY",
];

export const CONFLUX_PAYMENT_FALLBACK_ICON_URL = "/payment/methods/payment-fallback.png";
export const JAPAN_FALLBACK_WALLET_LABEL = "PayPay";

export const CONFLUX_PAYMENT_METHOD_META_MAP = {
  VISA: {
    label: "Visa",
    iconUrl: "/payment/methods/visa.png",
  },
  MASTER: {
    label: "Mastercard",
    iconUrl: "/payment/methods/mastercard.png",
  },
  MASTERCARD: {
    label: "Mastercard",
    iconUrl: "/payment/methods/mastercard.png",
  },
  APPLE_PAY: {
    label: "Apple Pay",
    iconUrl: "/payment/methods/apple-pay.png",
  },
  GOOGLE_PAY: {
    label: "Google Pay",
    iconUrl: "/payment/methods/google-pay.png",
  },
  CASHAPP_WEB: {
    label: JAPAN_FALLBACK_WALLET_LABEL,
    iconUrl: CONFLUX_PAYMENT_FALLBACK_ICON_URL,
  },
  CASHAPP_WAP: {
    label: JAPAN_FALLBACK_WALLET_LABEL,
    iconUrl: CONFLUX_PAYMENT_FALLBACK_ICON_URL,
  },
  CASH_APP: {
    label: JAPAN_FALLBACK_WALLET_LABEL,
    iconUrl: CONFLUX_PAYMENT_FALLBACK_ICON_URL,
  },
  KLARNA: {
    label: "Klarna",
    iconUrl: "/payment/methods/klarna.svg",
  },
  JCB: {
    label: "JCB",
    iconUrl: "/payment/methods/jcb.png",
  },
  AMEX: {
    label: "American Express",
    iconUrl: CONFLUX_PAYMENT_FALLBACK_ICON_URL,
  },
  AMERICAN_EXPRESS: {
    label: "American Express",
    iconUrl: CONFLUX_PAYMENT_FALLBACK_ICON_URL,
  },
  DISCOVER: {
    label: "Discover",
    iconUrl: CONFLUX_PAYMENT_FALLBACK_ICON_URL,
  },
};

const CONFLUX_CREDIT_CARD_CODE_ALIASES = {
  AMEX: ["AMEX", "AMERICAN_EXPRESS"],
  DISCOVER: ["DISCOVER"],
  JCB: ["JCB"],
  MASTERCARD: ["MASTERCARD", "MASTER"],
  VISA: ["VISA"],
};

export function normalizePaymentCode(value = "") {
  return `${value || ""}`.trim().toUpperCase();
}

export function detectConfluxCreditCardCode(value = "") {
  const digits = `${value || ""}`.replace(/\D/g, "");
  if (!digits) return "";

  if (/^(34|37)/.test(digits)) return "AMEX";
  if (/^(6011|65)/.test(digits)) return "DISCOVER";

  const first3 = Number(digits.slice(0, 3));
  if (digits.length >= 3 && first3 >= 644 && first3 <= 649) return "DISCOVER";

  const first4 = Number(digits.slice(0, 4));
  if (digits.length >= 4 && first4 >= 3528 && first4 <= 3589) return "JCB";
  if (digits.length >= 4 && first4 >= 2221 && first4 <= 2720) return "MASTERCARD";

  const first6 = Number(digits.slice(0, 6));
  if (digits.length >= 6 && first6 >= 622126 && first6 <= 622925) return "DISCOVER";

  const first2 = Number(digits.slice(0, 2));
  if (digits.length >= 2 && first2 >= 51 && first2 <= 55) return "MASTERCARD";

  if (digits.startsWith("4")) return "VISA";

  return "";
}

export function getConfluxCreditCardCodeAliases(code = "") {
  const normalizedCode = normalizePaymentCode(code);
  return CONFLUX_CREDIT_CARD_CODE_ALIASES[normalizedCode] || (normalizedCode ? [normalizedCode] : []);
}

export function isConfluxGenericCreditCardMethod(method = {}) {
  return Boolean(method?.isGenericCreditCard)
    || normalizePaymentCode(method?.code) === CONFLUX_GENERIC_CREDIT_CARD_CODE;
}

export function isConfluxCreditCardMethod(method = {}) {
  return Boolean(method?.isCard)
    || CONFLUX_CARD_PAYMENT_CODES.includes(normalizePaymentCode(method?.code))
    || CONFLUX_CARD_PAYMENT_CODES.includes(normalizePaymentCode(method?.wayCode));
}

export function normalizeConfluxPayTypeDisplay(value = "") {
  return `${value || ""}`
    .replace(/^Conflux支付[-:\s]*/i, "")
    .replace(/^Conflux\s*Pay[-:\s]*/i, "")
    .trim();
}

export function createConfluxFallbackWalletMethods(options = {}) {
  const cashAppCode = options.isPc ? "CASHAPP_WEB" : "CASHAPP_WAP";

  return [
    {
      code: cashAppCode,
      wayCode: cashAppCode,
      name: JAPAN_FALLBACK_WALLET_LABEL,
      returnType: "redirect",
      isCard: false,
      isWallet: true,
      isFallbackWallet: true,
    },
    {
      code: "KLARNA",
      wayCode: "KLARNA",
      name: "Klarna",
      returnType: "redirect",
      isCard: false,
      isWallet: true,
      isFallbackWallet: true,
    },
    {
      code: "APPLE_PAY",
      wayCode: "APPLE_PAY",
      name: "Apple Pay",
      returnType: "redirect",
      isCard: false,
      isWallet: true,
      isFallbackWallet: true,
    },
    {
      code: "GOOGLE_PAY",
      wayCode: "GOOGLE_PAY",
      name: "Google Pay",
      returnType: "redirect",
      isCard: false,
      isWallet: true,
      isFallbackWallet: true,
    },
  ];
}

export function createConfluxFallbackCardMethod(options = {}) {
  return {
    code: CONFLUX_GENERIC_CREDIT_CARD_CODE,
    wayCode: CONFLUX_GENERIC_CREDIT_CARD_CODE,
    name: options.creditCardLabel || "Credit Card",
    returnType: "redirect",
    isCard: true,
    isWallet: false,
    isFallback: true,
    isGenericCreditCard: true,
    cardMethods: [
      {
        code: "VISA",
        wayCode: "VISA",
        name: "Visa",
        displayName: "Visa",
        returnType: "redirect",
        isCard: true,
        isWallet: false,
        isFallback: true,
      },
    ],
  };
}

export function createConfluxGenericCreditCardMethod(cardMethods = [], options = {}) {
  const normalizedCardMethods = (cardMethods || [])
    .filter((method) => method?.code || method?.wayCode)
    .map((method) => ({
      ...method,
      code: normalizePaymentCode(method.code || method.wayCode),
      wayCode: normalizePaymentCode(method.wayCode || method.code),
      isCard: true,
      isWallet: false,
    }));
  const firstMethod = normalizedCardMethods[0] || {};
  const displaySeqList = normalizedCardMethods
    .map((method) => Number(method.displaySeq ?? method.display_seq))
    .filter(Number.isFinite);

  return {
    ...firstMethod,
    code: CONFLUX_GENERIC_CREDIT_CARD_CODE,
    wayCode: CONFLUX_GENERIC_CREDIT_CARD_CODE,
    name: options.creditCardLabel || "Credit Card",
    displayName: options.creditCardLabel || "Credit Card",
    iconUrl: "/payment/methods/visa.png",
    returnType: firstMethod.returnType || "redirect",
    isCard: true,
    isWallet: false,
    isGenericCreditCard: true,
    cardMethods: normalizedCardMethods,
    displaySeq: displaySeqList.length ? Math.min(...displaySeqList) : firstMethod.displaySeq,
    display_seq: displaySeqList.length ? Math.min(...displaySeqList) : firstMethod.display_seq,
  };
}

export function groupConfluxCreditCardMethods(methods = [], options = {}) {
  const cardMethods = [];
  const nonCardMethods = [];

  (methods || []).forEach((method) => {
    if (isConfluxCreditCardMethod(method)) {
      if (isConfluxGenericCreditCardMethod(method) && Array.isArray(method.cardMethods) && method.cardMethods.length) {
        cardMethods.push(...method.cardMethods);
      } else {
        cardMethods.push(method);
      }
      return;
    }
    nonCardMethods.push(method);
  });

  if (!cardMethods.length) return nonCardMethods;

  return [
    ...nonCardMethods,
    createConfluxGenericCreditCardMethod(cardMethods, options),
  ];
}

export function resolveConfluxCreditCardPaymentMethod(method = {}, cardNumber = "") {
  if (!method?.isCard) return method || null;
  if (!isConfluxGenericCreditCardMethod(method)) return method;

  const detectedCode = detectConfluxCreditCardCode(cardNumber);
  const aliases = getConfluxCreditCardCodeAliases(detectedCode);
  if (!aliases.length) return null;

  const matchedMethod = (method.cardMethods || []).find((cardMethod) => {
    const code = normalizePaymentCode(cardMethod.code);
    const wayCode = normalizePaymentCode(cardMethod.wayCode);
    return aliases.includes(code) || aliases.includes(wayCode);
  });
  if (matchedMethod) return matchedMethod;

  const code = aliases[0];
  const methodMeta = CONFLUX_PAYMENT_METHOD_META_MAP[code] || null;
  return {
    ...method,
    code,
    wayCode: code,
    name: methodMeta?.label || code,
    displayName: methodMeta?.label || code,
    iconUrl: methodMeta?.iconUrl || method.iconUrl,
    isGenericCreditCard: false,
    resolvedFromGenericCreditCard: true,
  };
}

export function normalizeConfluxPaymentMethod(item = {}, options = {}) {
  const methodConfigValue = parseConfluxJsonValue(
    item.otherConfigJson
    || item.other_config_json
    || item.configJson
    || item.config_json
    || item.config
  );
  const methodConfig = methodConfigValue && typeof methodConfigValue === "object" && !Array.isArray(methodConfigValue)
    ? methodConfigValue
    : {};
  const rawCode = item.paymentMethodCode
    || item.payment_method_code
    || item.methodCode
    || item.method_code
    || methodConfig.way_code
    || methodConfig.wayCode
    || item.wayCode
    || item.way_code
    || item.code
    || item.value
    || "";
  const code = normalizePaymentCode(rawCode);
  const wayCode = normalizePaymentCode(
    item.wayCode || item.way_code || methodConfig.way_code || methodConfig.wayCode || code
  );
  const name = item.paymentMethodName
    || item.payment_method_name
    || item.methodName
    || item.method_name
    || item.wayName
    || item.way_name
    || item.displayName
    || item.display_name
    || item.name
    || item.title
    || code;
  const payTypeDisplay = item.payTypeDisplay
    || item.pay_type_display
    || item.payTypeName
    || item.pay_type_name
    || "";
  const returnType = `${item.returnType || item.return_type || methodConfig.return_type || methodConfig.returnType || methodConfig.result_type || methodConfig.resultType || item.resultType || item.result_type || item.type || ""}`
    .trim()
    .toLowerCase();
  const category = `${item.category || item.paymentType || item.payment_type || item.methodType || item.method_type || methodConfig.method_type || methodConfig.methodType || ""}`
    .trim()
    .toLowerCase();
  const methodMeta = CONFLUX_PAYMENT_METHOD_META_MAP[code] || CONFLUX_PAYMENT_METHOD_META_MAP[wayCode] || null;
  const displayName = methodMeta?.label || normalizeConfluxPayTypeDisplay(payTypeDisplay) || name;
  const isCard = CONFLUX_CARD_PAYMENT_CODES.includes(code)
    || CONFLUX_CARD_PAYMENT_CODES.includes(wayCode)
    || category.includes("card")
    || `${name}`.toLowerCase().includes("card")
    || `${name}`.includes("カード");
  const isWallet = !isCard && (
    CONFLUX_WALLET_PAYMENT_CODES.includes(code)
    || CONFLUX_WALLET_PAYMENT_CODES.includes(wayCode)
    || category.includes("wallet")
    || category.includes("pay")
    || ["redirect", "qrcode", "qr_code", "qr"].includes(returnType)
  );

  return {
    ...item,
    code,
    wayCode,
    payTypeDisplay,
    otherConfig: methodConfig,
    currency: item.payCurrencyCode || item.pay_currency_code || item.currency || methodConfig.currency || "",
    name: displayName,
    displayName,
    iconUrl: methodMeta?.iconUrl || options.fallbackIconUrl || CONFLUX_PAYMENT_FALLBACK_ICON_URL,
    returnType: returnType || (isCard ? "redirect" : ""),
    isCard,
    isWallet,
  };
}

export function extractConfluxPaymentMethodList(response = {}) {
  const payload = response?.data ?? response;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.dataList)) return payload.dataList;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.paymentMethodList)) return payload.paymentMethodList;
  if (Array.isArray(payload?.paymentMethods)) return payload.paymentMethods;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.dataList)) return payload.data.dataList;
  if (Array.isArray(payload?.data?.list)) return payload.data.list;
  if (Array.isArray(payload?.data?.paymentMethodList)) return payload.data.paymentMethodList;

  return [];
}

export function buildNormalizedConfluxPaymentMethodList(rawMethods = [], options = {}) {
  const methods = (rawMethods || [])
    .map((method) => normalizeConfluxPaymentMethod(method, options))
    .filter((method) => method.code);
  const hasWalletMethod = methods.some((method) => (
    method.isWallet
    || CONFLUX_WALLET_PAYMENT_CODES.includes(normalizePaymentCode(method.code))
  ));
  const hasCardMethod = methods.some((method) => isConfluxCreditCardMethod(method));
  const fallbackWalletMethods = createConfluxFallbackWalletMethods(options);
  const fallbackCardMethod = createConfluxFallbackCardMethod(options);
  const mergedMethods = options.includeFallbacks === false
    ? methods
    : methods.length
      ? [
          ...(hasWalletMethod ? [] : fallbackWalletMethods),
          ...methods,
          ...(hasCardMethod ? [] : [fallbackCardMethod]),
        ]
      : [...fallbackWalletMethods, fallbackCardMethod];
  const visibleMethods = options.groupCreditCards === false
    ? mergedMethods
    : groupConfluxCreditCardMethods(mergedMethods, options);

  return visibleMethods
    .reduce((list, method) => {
      if (!list.some((item) => normalizePaymentCode(item.code) === normalizePaymentCode(method.code))) {
        list.push(method);
      }
      return list;
    }, [])
    .sort((a, b) => {
      const seqA = Number(a.displaySeq ?? a.display_seq ?? 9999);
      const seqB = Number(b.displaySeq ?? b.display_seq ?? 9999);
      if (seqA !== seqB) return seqA - seqB;
      return `${a.code || ""}`.localeCompare(`${b.code || ""}`);
    });
}

export function parseConfluxJsonValue(value) {
  if (typeof value !== "string") return value;

  const trimmedValue = value.trim();
  if (!trimmedValue || !/^[{[]/.test(trimmedValue)) return value;

  try {
    return JSON.parse(trimmedValue);
  } catch (error) {
    return value;
  }
}

export function getConfluxPayloadRoot(response = {}) {
  return parseConfluxJsonValue(response?.data ?? response);
}

export function pickConfluxStringValue(payload, paths = []) {
  for (const path of paths) {
    const value = get(payload, path);
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return "";
}

export function isConfluxNavigationUrl(value = "") {
  const url = `${value || ""}`.trim();
  return /^https?:\/\//i.test(url) || /^\/(?!\/)/.test(url);
}

export function collectConfluxResultTargets(response = {}) {
  const targets = [];
  const seen = new Set();

  const addTarget = (value, depth = 0) => {
    const parsedValue = parseConfluxJsonValue(value);

    if (!parsedValue || typeof parsedValue !== "object" || Array.isArray(parsedValue)) {
      return;
    }

    const targetKey = (() => {
      try {
        return JSON.stringify(parsedValue);
      } catch (error) {
        return String(parsedValue);
      }
    })();

    if (seen.has(targetKey)) return;
    seen.add(targetKey);
    targets.push(parsedValue);

    if (depth >= 2) return;

    [
      "redirectUrl",
      "redirect_url",
      "payUrl",
      "paymentUrl",
      "checkoutUrl",
      "authUrl",
      "url",
      "actionUrl",
      "html",
      "formHtml",
      "paymentHtml",
      "data",
      "result",
    ].forEach((path) => {
      const nestedValue = get(parsedValue, path);
      const nestedParsedValue = parseConfluxJsonValue(nestedValue);
      if (nestedParsedValue && typeof nestedParsedValue === "object") {
        addTarget(nestedParsedValue, depth + 1);
      }
    });
  };

  addTarget(getConfluxPayloadRoot(response));
  addTarget(response);
  return targets;
}

export function pickConfluxStringValueFromTargets(targets = [], paths = [], predicate = (value) => !!value) {
  for (const target of targets) {
    const value = pickConfluxStringValue(target, paths);
    if (value && predicate(value)) {
      return value;
    }
  }
  return "";
}

export function resolveConfluxPaymentResult(response = {}) {
  const payload = getConfluxPayloadRoot(response);
  const stringPayload = typeof payload === "string" ? payload.trim() : "";
  const targets = collectConfluxResultTargets(response);
  const redirectUrl = stringPayload && isConfluxNavigationUrl(stringPayload)
    ? stringPayload
    : pickConfluxStringValueFromTargets(targets, [
        "redirectUrl",
        "redirect_url",
        "data.redirectUrl",
        "data.redirect_url",
        "payUrl",
        "paymentUrl",
        "checkoutUrl",
        "authUrl",
        "url",
        "actionUrl",
        "action.url",
        "redirect.url",
        "threeDS.url",
        "three_ds.url",
        "nextAction.url",
        "next_action.url",
        "data.payUrl",
        "data.paymentUrl",
        "data.checkoutUrl",
        "data.authUrl",
        "data.url",
      ], isConfluxNavigationUrl);
  const qrCode = pickConfluxStringValueFromTargets(targets, [
    "qrcode",
    "qrCode",
    "qr_code",
    "codeUrl",
    "code_url",
    "data.qrcode",
    "data.qrCode",
    "data.qr_code",
    "data.codeUrl",
    "data.code_url",
  ]);
  const html = stringPayload && /<form|<html|<body/i.test(stringPayload)
    ? stringPayload
    : pickConfluxStringValueFromTargets(targets, [
        "html",
        "formHtml",
        "paymentHtml",
        "data.html",
        "data.formHtml",
        "data.paymentHtml",
      ]);

  return {
    redirectUrl,
    qrCode,
    html,
  };
}

export function getConfluxPaymentMethodDisplayName(method = {}, translate) {
  const code = normalizePaymentCode(method.code || method.wayCode);
  const methodMeta = CONFLUX_PAYMENT_METHOD_META_MAP[code] || null;
  const fallback = typeof translate === "function"
    ? translate("payment.confluxWalletBalance")
    : "Wallet Balance";

  return method.displayName || method.name || methodMeta?.label || fallback;
}

export function getConfluxPaymentMethodDescription(method = {}, translate) {
  if (typeof translate !== "function") {
    if (method.isCard) return "Credit Card";
    if (["qrcode", "qr_code", "qr"].includes(method.returnType)) return "Pay by QR code";
    return "Redirect payment";
  }

  if (method.isCard) return translate("payment.confluxCreditCard");
  if (method.returnType === "qrcode" || method.returnType === "qr_code" || method.returnType === "qr") {
    return translate("payment.confluxPayByQr");
  }
  return translate("payment.confluxRedirectToWallet");
}

export default {
  CONFLUX_GENERIC_CREDIT_CARD_CODE,
  detectConfluxCreditCardCode,
  getConfluxCreditCardCodeAliases,
  isConfluxGenericCreditCardMethod,
  isConfluxCreditCardMethod,
  groupConfluxCreditCardMethods,
  resolveConfluxCreditCardPaymentMethod,
  normalizePaymentCode,
  normalizeConfluxPaymentMethod,
  extractConfluxPaymentMethodList,
  buildNormalizedConfluxPaymentMethodList,
  resolveConfluxPaymentResult,
  getConfluxPaymentMethodDisplayName,
  getConfluxPaymentMethodDescription,
};
